import * as React from "react";
import { Dialog as SheetPrimitive } from "radix-ui";
import { XIcon } from "lucide-react";
import { cn } from "src/shadcn/lib/utils";
import { Button } from "src/shadcn/components/button";

import {
  Sheet as ShadcnSheet,
  SheetTrigger as ShadcnSheetTrigger,
  SheetClose as ShadcnSheetClose,
  SheetHeader as ShadcnSheetHeader,
  SheetFooter as ShadcnSheetFooter,
  SheetTitle as ShadcnSheetTitle,
  SheetDescription as ShadcnSheetDescription,
} from "src/shadcn/components/sheet";

const Sheet = (props: React.ComponentProps<typeof ShadcnSheet>) => (
  <ShadcnSheet {...props} />
);

const SheetTrigger = (
  props: React.ComponentProps<typeof ShadcnSheetTrigger>
) => <ShadcnSheetTrigger {...props} />;

const SheetClose = (props: React.ComponentProps<typeof ShadcnSheetClose>) => (
  <ShadcnSheetClose {...props} />
);

/**
 * SheetContent composes Portal + Overlay + Radix Content directly so
 * the hardcoded max-w-sm is removed and width is fully consumer-controlled.
 */
const SheetContent = ({
  className,
  children,
  side = "right",
  showCloseButton = true,
  overlayClassName,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  showCloseButton?: boolean;
  overlayClassName?: string;
}) => (
  <SheetPrimitive.Portal data-slot="sheet-portal">
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/10 duration-100 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
        overlayClassName
      )}
    />
    <SheetPrimitive.Content
      data-slot="sheet-content"
      data-side={side}
      className={cn(
        "fixed z-50 flex flex-col gap-4 bg-popover bg-clip-padding text-sm text-popover-foreground shadow-lg outline-none transition duration-200 ease-in-out",
        "data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:border-border",
        "data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-e data-[side=left]:border-border data-[side=left]:sm:max-w-sm",
        "data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-s data-[side=right]:border-border data-[side=right]:sm:max-w-sm",
        "data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:border-border",
        "data-open:animate-in data-open:fade-in-0",
        "data-[side=bottom]:data-open:slide-in-from-bottom-10 data-[side=left]:data-open:slide-in-from-left-10 data-[side=right]:data-open:slide-in-from-right-10 data-[side=top]:data-open:slide-in-from-top-10",
        "data-closed:animate-out data-closed:fade-out-0",
        "data-[side=bottom]:data-closed:slide-out-to-bottom-10 data-[side=left]:data-closed:slide-out-to-left-10 data-[side=right]:data-closed:slide-out-to-right-10 data-[side=top]:data-closed:slide-out-to-top-10",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <SheetPrimitive.Close data-slot="sheet-close" asChild>
          <Button
            variant="ghost"
            className="absolute top-3 end-3"
            size="icon-sm"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </Button>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  </SheetPrimitive.Portal>
);

const SheetHeader = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnSheetHeader>) => (
  <ShadcnSheetHeader className={cn("gap-3 p-6", className)} {...props} />
);

const SheetFooter = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnSheetFooter>) => (
  <ShadcnSheetFooter
    className={cn("bg-muted/50 px-6 py-4", className)}
    {...props}
  />
);

const SheetTitle = (props: React.ComponentProps<typeof ShadcnSheetTitle>) => (
  <ShadcnSheetTitle {...props} />
);

const SheetDescription = (
  props: React.ComponentProps<typeof ShadcnSheetDescription>
) => <ShadcnSheetDescription {...props} />;

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
